import { Validators } from '@biteinc/common';
import { allEnumValues, BiteAdminMfaOption, CustomerAuthProvider, FulfillmentMethod, IntegrationSystem, MdmProvider, OrgOrderChannelState, UrlTarget, UserRight, Weekday, } from '@biteinc/enums';
import { AbstractModelSchema } from './abstract_model_schema.js';
const customDomainSchema = {
    displayName: 'custom domain',
    type: 'object',
    fields: {
        domain: {
            type: 'keyString',
            displayName: 'Custom Domain',
            biteOnly: true,
            tooltip: {
                text: 'This is a custom subdomain that will be used for Web Ordering e.g. order.getbite.com',
            },
            validationRegex: '^(?=.{4,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\\.)+[a-z]{2,18}$',
        },
        id: {
            type: 'keyString',
            isHidden: true,
            isReadOnly: true,
        },
        cname: {
            type: 'keyString',
            displayName: 'CNAME',
            isReadOnly: true,
            biteOnly: true,
            tooltip: {
                text: 'This should be added to DNS records as a CNAME record for the domain provided.',
            },
        },
        targetCname: {
            type: 'keyString',
            isHidden: true,
            isReadOnly: true,
        },
        status: {
            type: 'keyString',
            displayName: 'Status',
            isReadOnly: true,
            biteOnly: true,
        },
    },
};
const customDomainMetaDataSchema = {
    displayName: 'custom meta data',
    type: 'object',
    fields: {
        favicon: {
            displayName: 'Favicon',
            type: 'array',
            elementType: 'object',
            elementSubtype: 'Image',
            elementUniqueIdFieldName: 'origUrl',
            maxCount: 1,
            keepAspectRatio: 'contain',
            imagePrefix: 'favicon',
            size: {
                width: 64,
                height: 64,
            },
        },
        appleTouchIcon: {
            displayName: 'Apple Touch Icon',
            type: 'array',
            elementType: 'object',
            elementSubtype: 'Image',
            elementUniqueIdFieldName: 'origUrl',
            maxCount: 1,
            keepAspectRatio: 'contain',
            imagePrefix: 'apple-touch-icon',
            size: {
                width: 152,
                height: 152,
            },
        },
        ogTitle: {
            type: 'keyString',
            displayName: 'OpenGraph Title',
            tooltip: {
                text: 'The title seen by web crawlers and when sharing',
            },
        },
        ogDescription: {
            type: 'keyString',
            displayName: 'OpenGraph Description',
            tooltip: {
                text: 'The description seen by web crawlers and when sharing',
            },
        },
        ogImage: {
            displayName: 'OpenGraph Image',
            type: 'array',
            elementType: 'object',
            elementSubtype: 'Image',
            elementUniqueIdFieldName: 'origUrl',
            maxCount: 1,
            keepAspectRatio: 'contain',
            imagePrefix: 'og-image',
            size: {
                width: 1200,
                height: 630,
            },
        },
    },
};
function getHomeScreenImageFieldSchemas(imageSpecByFieldName) {
    const imageFieldSchemaByName = {};
    Object.keys(imageSpecByFieldName).forEach((imageFieldName) => {
        const { displayName, width, height, required } = imageSpecByFieldName[imageFieldName];
        imageFieldSchemaByName[imageFieldName] = {
            displayName,
            type: 'array',
            elementType: 'object',
            elementSubtype: 'Image',
            elementUniqueIdFieldName: 'origUrl',
            ...(required && {
                required: true,
                minCount: 1,
            }),
            maxCount: 1,
            imagePrefix: 'home-screen-img',
            keepAspectRatio: 'contain',
            size: {
                width,
                height,
            },
        };
    });
    return {
        title: {
            type: 'keyString',
            tooltip: {
                text: 'This title is purely for being able to easily identify the image in the Admin Portal. It is not meant to be user-facing.',
            },
        },
        isEnabled: {
            type: 'bool',
            displayName: 'Enabled',
        },
        ...imageFieldSchemaByName,
        availability: {
            type: 'object',
            fields: {
                dateRanges: {
                    displayName: 'Specific Date Ranges',
                    type: 'array',
                    elementType: 'object',
                    elementSubtype: 'DateRange',
                    ui: 'accordion',
                    uiSubtype: 'DateRange',
                },
                daySchedules: {
                    displayName: 'Specific Times in the Week',
                    type: 'array',
                    elementType: 'object',
                    ui: 'accordion',
                    uiSubtype: 'DaySchedule',
                    fields: {
                        weekday: {
                            type: 'stringEnum',
                            enum: Weekday,
                            required: true,
                            isHidden: true,
                        },
                        schedules: {
                            type: 'array',
                            elementType: 'object',
                            elementSubtype: 'Schedule',
                            required: true,
                            minCount: 1,
                            ui: 'accordion',
                            uiSubtype: 'Schedule',
                        },
                    },
                },
            },
        },
    };
}
export const orgSchema = {
    displayName: 'org',
    type: 'object',
    fields: {
        ...AbstractModelSchema.fields,
        name: {
            type: 'keyString',
            required: true,
            biteOnly: true,
            tabId: 'org-settings',
        },
        customerAccountsScope: {
            type: 'keyString',
            biteOnly: true,
            displayName: 'Customer Accounts Scope',
            validationRegex: '^[a-z0-9]+(?:-[a-z0-9]+)*$',
            tooltip: {
                text: 'This is a unique slug used to scope accounts for an org. E.g. bite, bite-lab',
            },
            tabId: 'org-settings',
        },
        orgCode: {
            type: 'keyString',
            biteOnly: true,
            displayName: 'Org Code',
            validationRegex: '^[a-z0-9-_]{4,64}$',
            tooltip: {
                text: 'The code a guest types into the mobile app to see every location in this org. ' +
                    'Lowercase alphanumeric, underscores, and hyphens. 4-64 characters. ' +
                    'Setting it turns on the locations screen for this org. ' +
                    'It must not match any location url slug, because org codes are resolved first.',
            },
            tabId: 'org-settings',
        },
        webEnabled: {
            type: 'bool',
            displayName: 'Web Enabled',
            biteOnly: true,
            tooltip: {
                text: 'Uses a landing page for location discovery.',
            },
            tabId: 'org-settings',
        },
        recoBridgeEnabled: {
            type: 'bool',
            displayName: 'Reco Bridge Enabled',
            biteOnly: true,
            tooltip: {
                text: 'Enables automated cold-start recommendation rule generation (bridge) for this org. Generated runs still require a bureau release to serve.',
            },
            tabId: 'org-settings',
        },
        biteLogArchiveReadEnabled: {
            type: 'bool',
            displayName: 'Bite Log Archive Read Enabled',
            biteOnly: true,
            tooltip: {
                text: 'On by default: older bite log history comes from the S3 archive. Uncheck to keep this org on Mongo only. If it is already unchecked, check it and Save, then uncheck it and Save to store the opt-out. With LOG_ARCHIVE_READ_ORG_OPT_IN_ONLY=true, only checked orgs read the archive. LOG_ARCHIVE_READ_ENABLED must also be on.',
            },
            tabId: 'org-settings',
        },
        kioskStatusArchiveReadEnabled: {
            type: 'bool',
            displayName: 'Kiosk Status Archive Read Enabled',
            biteOnly: true,
            tooltip: {
                text: 'On by default: older kiosk status history comes from the S3 archive. Uncheck to keep this org on Mongo only. If it is already unchecked, check it and Save, then uncheck it and Save to store the opt-out. With LOG_ARCHIVE_READ_ORG_OPT_IN_ONLY=true, only checked orgs read the archive. KIOSK_STATUS_ARCHIVE_READ_ENABLED must also be on.',
            },
            tabId: 'org-settings',
        },
        mobileAppEnabled: {
            type: 'bool',
            displayName: 'Mobile App Enabled',
            biteOnly: true,
            tabId: 'org-settings',
        },
        biteAdminMfaOptions: {
            type: 'array',
            elementType: 'stringEnum',
            elementEnum: BiteAdminMfaOption,
            displayName: 'Bite Admin Multi-Factor Authentication',
            tabId: 'org-settings',
            // This field should be accessible to org Admins, but to err on the side of caution, we are
            // making this field Bite-Only for now, just so that an org Admin does not accidentally lock
            // themselves out of their account.
            // Once we have verified that all Bite Admin accounts are valid, we can consider making this
            // field accessible to org Admins as intended.
            // writeRight: UserRight.Admin,
            biteOnly: true,
            required: true,
            defaultValue: [BiteAdminMfaOption.Email],
            ui: 'checklist',
            tooltip: {
                text: 'Select the MFA options for Bite Admin. If no options are selected, MFA is disabled.',
            },
        },
        customerAuthProvider: {
            displayName: 'Auth Provider',
            type: 'stringEnum',
            enum: CustomerAuthProvider,
            biteOnly: true,
            required: true,
            defaultValue: 'bite',
            ui: 'dropdown',
            conditions: [
                {
                    field: 'webEnabled',
                    values: [true],
                },
            ],
            tabId: 'org-settings',
        },
        showSignupButton: {
            type: 'bool',
            displayName: 'Show Signup',
            biteOnly: true,
            tooltip: {
                text: 'Display a Sign Up button on the web landing page.',
            },
            conditions: [
                {
                    field: 'webEnabled',
                    values: [true],
                },
            ],
            tabId: 'org-settings',
        },
        signupUrl: {
            type: 'url',
            displayName: 'Signup Url',
            biteOnly: true,
            tooltip: {
                text: 'This is the url users will be directed to create an account at.',
            },
            conditions: [
                {
                    field: 'customerAuthProvider',
                    values: [CustomerAuthProvider.Paytronix],
                },
                {
                    field: 'showSignupButton',
                    values: [true],
                },
            ],
            tabId: 'org-settings',
        },
        brandColor: {
            type: 'keyString',
            writeRight: UserRight.ManageBrandColorAndLogo,
            displayName: 'Brand Color',
            placeholder: '#FF0000 for red or #FF00007F for red with 50% alpha',
            validationRegex: Validators.hexColorWithOptionalOpacityRegexString,
            usesModelValidation: true,
            tooltip: {
                text: 'This is the color used in Flash User Accounts for components such as headers.',
            },
            tabId: 'org-settings',
        },
        logo: {
            type: 'array',
            elementType: 'object',
            elementSubtype: 'Image',
            elementUniqueIdFieldName: 'origUrl',
            maxCount: 1,
            displayName: 'Logo',
            keepAspectRatio: 'contain',
            imagePrefix: 'logos',
            size: {
                width: 360,
                height: 240,
            },
            tooltip: {
                text: 'This image will be used in headers for the landing page and menu.',
            },
            tabId: 'org-settings',
            writeRight: UserRight.ManageBrandColorAndLogo,
        },
        loyaltyLogo: {
            type: 'array',
            elementType: 'object',
            elementSubtype: 'Image',
            elementUniqueIdFieldName: 'origUrl',
            maxCount: 1,
            displayName: 'Loyalty Logo',
            keepAspectRatio: 'contain',
            imagePrefix: 'logos',
            size: {
                width: 360,
                height: 240,
            },
            tooltip: {
                text: 'This image will be used on login and signup pages for customer accounts.',
            },
            tabId: 'org-settings',
            writeRight: UserRight.ManageBrandColorAndLogo,
        },
        appearanceId: {
            type: 'mongoId',
            biteOnly: true,
            displayName: 'Appearance',
            tooltip: {
                text: 'This will affect org-level pages not tied to a specific location.',
            },
            tabId: 'org-settings',
            ui: 'dropdown',
        },
        enterpriseTagId: {
            type: 'mongoId',
            biteOnly: true,
            displayName: 'Enterprise Tag',
            tooltip: {
                text: 'Groups this org with others under the same enterprise (e.g. Aramark, Love’s). Drives cross-org magic copy permissions.',
            },
            tabId: 'org-settings',
            ui: 'dropdown',
            writeRight: UserRight.BiteOps,
        },
        mdmSettings: {
            type: 'array',
            biteOnly: true,
            displayName: 'MDM Settings',
            elementType: 'object',
            ui: 'accordion',
            uiSubtype: 'MdmSettings',
            fields: {
                mdmProvider: {
                    type: 'stringEnum',
                    enum: MdmProvider,
                    required: true,
                    isHidden: true,
                },
                eloView4OrgId: {
                    type: 'keyString',
                    displayName: 'EloView4 Org ID',
                    required: true,
                    conditions: [
                        {
                            field: 'mdmProvider',
                            values: [MdmProvider.EloView],
                        },
                    ],
                },
                eloView4ClientId: {
                    type: 'keyString',
                    displayName: 'EloView4 ClientId',
                    required: true,
                    conditions: [
                        {
                            field: 'mdmProvider',
                            values: [MdmProvider.EloView],
                        },
                    ],
                },
                eloView4ClientSecret: {
                    type: 'keyString',
                    displayName: 'EloView4 Client Secret',
                    required: true,
                    conditions: [
                        {
                            field: 'mdmProvider',
                            values: [MdmProvider.EloView],
                        },
                    ],
                },
                signageOsTokenId: {
                    type: 'keyString',
                    displayName: 'Auth TokenID',
                    required: true,
                    conditions: [
                        {
                            field: 'mdmProvider',
                            values: [MdmProvider.SignageOS],
                        },
                    ],
                },
                signageOsTokenSecret: {
                    type: 'keyString',
                    displayName: 'Auth Token Secret',
                    required: true,
                    conditions: [
                        {
                            field: 'mdmProvider',
                            values: [MdmProvider.SignageOS],
                        },
                    ],
                },
                workspaceOneAccountId: {
                    type: 'keyString',
                    displayName: 'Account ID',
                    required: true,
                    conditions: [
                        {
                            field: 'mdmProvider',
                            values: [MdmProvider.WorkspaceOne],
                        },
                    ],
                    tooltip: {
                        text: 'This value gets used in the W1 API host: https://asXXXX.awmdm.com/API',
                    },
                },
                workspaceOneClientId: {
                    type: 'keyString',
                    displayName: 'Client ID',
                    required: true,
                    conditions: [
                        {
                            field: 'mdmProvider',
                            values: [MdmProvider.WorkspaceOne],
                        },
                    ],
                },
                workspaceOneClientSecret: {
                    type: 'keyString',
                    displayName: 'Client Secret',
                    required: true,
                    conditions: [
                        {
                            field: 'mdmProvider',
                            values: [MdmProvider.WorkspaceOne],
                        },
                    ],
                },
            },
            tabId: 'org-settings',
        },
        webhookSettings: {
            type: 'array',
            biteOnly: true,
            displayName: 'Webhook Settings',
            elementType: 'object',
            ui: 'accordion',
            uiSubtype: 'WebhookSettings',
            fields: {
                system: {
                    type: 'int',
                    values: allEnumValues(IntegrationSystem),
                    required: true,
                    isHidden: true,
                },
                oloSecretKey: {
                    type: 'keyString',
                    displayName: 'Secret Key',
                    required: true,
                    conditions: [
                        {
                            field: 'system',
                            values: [IntegrationSystem.Olo],
                        },
                    ],
                },
            },
            tabId: 'org-settings',
        },
        thirdPartyPrivacyPolicyHtml: {
            type: 'codeString',
            displayName: '3rd-Party Privacy Policy HTML (Web)',
            tooltip: {
                text: 'This blurb will show up at the bottom of the Bite Flash Privacy Policy page.',
            },
            tabId: 'org-settings',
            writeRight: UserRight.ManageThirdPartyPrivacyPolicy,
        },
        thirdPartyPolicyLinks: {
            type: 'array',
            elementType: 'object',
            displayName: '3rd-Party Policy Links (Web)',
            ui: 'accordion',
            uiSubtype: 'Links',
            tooltip: {
                text: 'These links will show up at the bottom of the flash page along with Bite privacy policy links.',
            },
            fields: {
                title: {
                    type: 'keyString',
                    required: true,
                    writeRight: UserRight.ManageThirdPartyPrivacyPolicy,
                },
                url: {
                    type: 'url',
                    required: true,
                    displayName: 'URL',
                    placeholder: 'https://www.your-website.com/policy',
                    writeRight: UserRight.ManageThirdPartyPrivacyPolicy,
                },
            },
            tabId: 'org-settings',
            writeRight: UserRight.ManageThirdPartyPrivacyPolicy,
        },
        thirdPartyKioskPrivacyPolicyText: {
            type: 'longString',
            displayName: '3rd-Party Privacy Policy Text (Kiosk)',
            tooltip: {
                text: 'This blurb will show up at the bottom when viewing the Privacy Policy on the Kiosk.',
            },
            canHaveLocalizationOverrides: true,
            tabId: 'org-settings',
            writeRight: UserRight.ManageThirdPartyPrivacyPolicy,
        },
        thirdPartyLoyaltySignupPrivacyPolicyText: {
            type: 'longString',
            displayName: '3rd-Party Privacy Policy Text (Loyalty Signup)',
            tooltip: {
                text: 'This blurb will show up when viewing Privacy Policy during loyalty signup.',
            },
            canHaveLocalizationOverrides: true,
            tabId: 'org-settings',
            writeRight: UserRight.ManageThirdPartyPrivacyPolicy,
        },
        googleTagManagerId: {
            type: 'keyString',
            displayName: 'Google Tag Manager ID',
            tabId: 'org-settings',
            validationRegex: '^GTM-[a-zA-Z0-9]+$',
            tooltip: {
                text: 'Google Tag Manager container ID.',
            },
            conditions: [
                {
                    field: 'webEnabled',
                    values: [true],
                },
            ],
        },
        includeInDailyAccountHealthSummary: {
            type: 'bool',
            displayName: 'Include in Daily Account Health Summary?',
            tabId: 'org-settings',
        },
        contactInfo: {
            displayName: 'Contact Info',
            type: 'longString',
            biteOnly: true,
            placeholder: 'Store contact info like manager name, phone number, address.',
            tabId: 'org-settings',
        },
        testCredentials: {
            displayName: 'Test Credentials',
            type: 'longString',
            biteOnly: true,
            maskInput: true,
            tabId: 'org-settings',
            placeholder: 'Save any test creds for loyalty and gift cards here',
        },
        webCustomDomain: {
            type: 'object',
            displayName: 'Custom Domain',
            biteOnly: true,
            tabId: 'web-settings',
            writeRight: UserRight.ManageWebSettings,
            fields: customDomainSchema.fields,
        },
        webFulfillmentMethods: {
            type: 'array',
            elementType: 'int',
            elementValues: allEnumValues(FulfillmentMethod),
            displayName: 'Dining Options',
            ui: 'table',
            placeholder: 'Select a dining option to add',
            tabId: 'web-settings',
            writeRight: UserRight.ManageWebSettings,
        },
        webOrderChannelState: {
            displayName: 'State',
            type: 'stringEnum',
            enum: OrgOrderChannelState,
            required: true,
            biteOnly: true,
            ui: 'dropdown',
            tabId: 'web-settings',
            writeRight: UserRight.ManageWebSettings,
        },
        webLocationIds: {
            type: 'array',
            elementType: 'mongoId',
            required: true,
            displayName: 'Web Locations',
            ui: 'table',
            placeholder: 'Select Locations',
            tabId: 'web-settings',
            conditions: [
                {
                    field: 'webOrderChannelState',
                    values: [OrgOrderChannelState.Draft],
                },
            ],
            writeRight: UserRight.ManageWebSettings,
        },
        webCheckoutDesktopPromoImage: {
            type: 'array',
            elementType: 'object',
            elementSubtype: 'Image',
            elementUniqueIdFieldName: 'origUrl',
            maxCount: 1,
            displayName: 'Desktop Checkout Page Promo Image',
            keepAspectRatio: 'contain',
            imagePrefix: 'web',
            size: {
                width: 1024,
                height: 576,
            },
            tooltip: {
                text: 'This image will be used on the Desktop checkout page for Web when a guest is not logged in.',
            },
            tabId: 'web-settings',
            writeRight: UserRight.ManageWebSettings,
        },
        webHomeScreenImages: {
            type: 'array',
            elementType: 'object',
            displayName: 'Home Screen Images',
            ui: 'accordion',
            uiSubtype: 'HomeScreenImages',
            fields: getHomeScreenImageFieldSchemas({
                mobileImage: {
                    displayName: 'Mobile Image',
                    width: 420 * 2,
                    height: 420 * 2,
                    // The API will require that one of these fields is provided
                    required: false,
                },
                desktopImage: {
                    displayName: 'Desktop Image',
                    width: 840 * 2,
                    height: 840 * 2,
                    // The API will require that one of these fields is provided
                    required: false,
                },
            }),
            tabId: 'web-settings',
            writeRight: UserRight.ManageWebSettings,
        },
        webCustomMapMarker: {
            type: 'array',
            elementType: 'object',
            elementSubtype: 'Image',
            elementUniqueIdFieldName: 'origUrl',
            maxCount: 1,
            displayName: 'Custom Map Marker',
            keepAspectRatio: 'contain',
            imagePrefix: 'map-marker',
            size: {
                width: 1024,
                height: 1024,
            },
            tooltip: {
                text: 'This image will be used on the landing page for Web on the map to indicate the location.',
            },
            tabId: 'web-settings',
            writeRight: UserRight.ManageWebSettings,
        },
        webCustomDomainMetaData: {
            type: 'object',
            displayName: 'Custom Meta Data',
            tabId: 'web-settings',
            writeRight: UserRight.ManageWebSettings,
            fields: customDomainMetaDataSchema.fields,
        },
        webWellKnownFileContents: {
            type: 'keyString',
            displayName: 'Apple Well Known File Contents',
            tooltip: {
                text: 'This is the contents of the apple-wellknown-merchant-association file. This is only used if there is a custom domain.',
            },
            biteOnly: true,
            tabId: 'web-settings',
        },
        webWellKnownFileContentsMassEnablement: {
            type: 'bool',
            displayName: 'Well Known File Contents uses Mass Enablement',
            tooltip: {
                text: 'If file has no extension then this is true. If .txt then this is false.',
                isWarning: true,
            },
            defaultValue: true,
            biteOnly: true,
            tabId: 'web-settings',
        },
        locationSearchIsEnabled: {
            type: 'bool',
            displayName: 'Location Search is Enabled',
            tabId: 'web-settings',
            tooltip: {
                text: 'This does not enable location search, but is used to tell the web page if it should include links to the location search page.',
            },
        },
        cateringCustomDomain: {
            type: 'object',
            displayName: 'Custom Domain',
            biteOnly: true,
            tabId: 'catering-settings',
            fields: customDomainSchema.fields,
        },
        cateringFulfillmentMethods: {
            type: 'array',
            elementType: 'int',
            elementValues: allEnumValues(FulfillmentMethod),
            displayName: 'Dining Options',
            ui: 'table',
            placeholder: 'Select a dining option to add',
            tabId: 'catering-settings',
            writeRight: UserRight.ManageCateringSettings,
        },
        cateringOrderChannelState: {
            displayName: 'State',
            type: 'stringEnum',
            enum: OrgOrderChannelState,
            required: true,
            biteOnly: true,
            ui: 'dropdown',
            tabId: 'catering-settings',
        },
        cateringLocationIds: {
            type: 'array',
            elementType: 'mongoId',
            displayName: 'Catering Locations',
            ui: 'table',
            placeholder: 'Select Locations',
            tabId: 'catering-settings',
            conditions: [
                {
                    field: 'cateringOrderChannelState',
                    values: [OrgOrderChannelState.Draft],
                },
            ],
            writeRight: UserRight.ManageCateringSettings,
        },
        cateringHomeScreenImages: {
            type: 'array',
            elementType: 'object',
            displayName: 'Home Screen Images',
            ui: 'accordion',
            uiSubtype: 'HomeScreenImages',
            fields: getHomeScreenImageFieldSchemas({
                mobileImage: {
                    displayName: 'Mobile Image',
                    width: 420 * 2,
                    height: 420 * 2,
                    // The API will require that one of these fields is provided
                    required: false,
                },
                desktopImage: {
                    displayName: 'Desktop Image',
                    width: 840 * 2,
                    height: 840 * 2,
                    // The API will require that one of these fields is provided
                    required: false,
                },
            }),
            tabId: 'catering-settings',
            writeRight: UserRight.ManageCateringSettings,
        },
        cateringCheckoutDesktopPromoImage: {
            type: 'array',
            elementType: 'object',
            elementSubtype: 'Image',
            elementUniqueIdFieldName: 'origUrl',
            maxCount: 1,
            displayName: 'Desktop Checkout Page Promo Image',
            keepAspectRatio: 'contain',
            imagePrefix: 'web',
            size: {
                width: 1024,
                height: 576,
            },
            tooltip: {
                text: 'This image will be used on the Desktop checkout page for Web when a guest is not logged in.',
            },
            tabId: 'catering-settings',
            writeRight: UserRight.ManageWebSettings,
        },
        cateringCustomMapMarker: {
            type: 'array',
            elementType: 'object',
            elementSubtype: 'Image',
            elementUniqueIdFieldName: 'origUrl',
            maxCount: 1,
            displayName: 'Custom Map Marker',
            keepAspectRatio: 'contain',
            imagePrefix: 'map-marker',
            size: {
                width: 1024,
                height: 1024,
            },
            tooltip: {
                text: 'This image will be used on the landing page for Catering on the map to indicate the location.',
            },
            tabId: 'catering-settings',
            writeRight: UserRight.ManageCateringSettings,
        },
        cateringCustomDomainMetaData: {
            type: 'object',
            displayName: 'Custom Meta Data',
            tabId: 'catering-settings',
            writeRight: UserRight.ManageCateringSettings,
            fields: customDomainMetaDataSchema.fields,
        },
        cateringWellKnownFileContents: {
            type: 'keyString',
            displayName: 'Apple Well Known File Contents',
            tooltip: {
                text: 'This is the contents of the apple-wellknown-merchant-association file. This is only used if there is a custom domain.',
            },
            biteOnly: true,
            tabId: 'catering-settings',
        },
        cateringWellKnownFileContentsMassEnablement: {
            type: 'bool',
            displayName: 'Well Known File Contents uses Mass Enablement',
            tooltip: {
                text: 'If file has no extension then this is true. If .txt then this is false.',
                isWarning: true,
            },
            defaultValue: true,
            biteOnly: true,
            tabId: 'catering-settings',
        },
        mobileAppHomeScreenImages: {
            type: 'array',
            elementType: 'object',
            displayName: 'Home Screen Images',
            ui: 'accordion',
            uiSubtype: 'HomeScreenImages',
            fields: getHomeScreenImageFieldSchemas({
                image: {
                    displayName: 'Image',
                    width: 1623,
                    height: 2436,
                    required: true,
                },
            }),
            tabId: 'mobile-app-settings',
            writeRight: UserRight.Admin,
        },
        mobileAppContactUsInfo: {
            type: 'object',
            displayName: 'Contact Us Information',
            fields: {
                email: {
                    type: 'email',
                    displayName: 'Email Address',
                },
                phoneNumber: {
                    type: 'phone',
                    displayName: 'Phone Number',
                    tooltip: {
                        text: 'Please provide a 10-digit number without any spaces or brackets; no country code. (e.g. 4161234567)',
                    },
                },
            },
            tabId: 'mobile-app-settings',
            writeRight: UserRight.Admin,
        },
        mobileAppSidebarLinks: {
            type: 'array',
            elementType: 'object',
            displayName: 'Sidebar Links',
            ui: 'accordion',
            uiSubtype: 'Links',
            fields: {
                title: {
                    type: 'keyString',
                    required: true,
                },
                url: {
                    displayName: 'URL',
                    type: 'url',
                    required: true,
                    placeholder: 'https://www.your-website.com/allergen-info',
                },
                target: {
                    displayName: 'Open in',
                    type: 'stringEnum',
                    enum: UrlTarget,
                    required: true,
                    ui: 'dropdown',
                },
            },
            tabId: 'mobile-app-settings',
            writeRight: UserRight.Admin,
        },
        mobileAppPolicyLinks: {
            type: 'array',
            elementType: 'object',
            displayName: 'Policy Links',
            ui: 'accordion',
            uiSubtype: 'Links',
            fields: {
                title: {
                    type: 'keyString',
                    required: true,
                },
                url: {
                    type: 'url',
                    required: true,
                    displayName: 'URL',
                    placeholder: 'https://www.your-website.com/policy',
                },
                target: {
                    displayName: 'Open in',
                    type: 'stringEnum',
                    enum: UrlTarget,
                    required: true,
                    ui: 'dropdown',
                },
            },
            tabId: 'mobile-app-settings',
            writeRight: UserRight.Admin,
        },
        mobileAppSocialMediaLinks: {
            type: 'array',
            elementType: 'object',
            displayName: 'Social Media Links',
            ui: 'accordion',
            uiSubtype: 'Links',
            fields: {
                title: {
                    type: 'keyString',
                    required: true,
                },
                icon: {
                    type: 'array',
                    elementType: 'object',
                    elementSubtype: 'Image',
                    elementUniqueIdFieldName: 'origUrl',
                    required: true,
                    minCount: 1,
                    maxCount: 1,
                    keepAspectRatio: 'contain',
                    imagePrefix: 'social-media-icon',
                    size: {
                        width: 200,
                        height: 200,
                    },
                },
                url: {
                    type: 'url',
                    required: true,
                    displayName: 'URL',
                    placeholder: 'https://www.your-website.com/policy',
                },
            },
            tabId: 'mobile-app-settings',
            writeRight: UserRight.Admin,
        },
        adminCanAccessKor: {
            type: 'bool',
            displayName: 'Admin Can Access KOR',
            tabId: 'org-settings',
            biteOnly: true,
        },
        korSmsAlertsEnabled: {
            type: 'bool',
            displayName: 'KOR SMS Alerts Enabled',
            tabId: 'org-settings',
            biteOnly: true,
        },
    },
};
//# sourceMappingURL=org_schema.js.map